import { useEffect, useMemo } from 'react';
import { useEffectOnce } from 'react-use';
import { rage, useObservable } from '~/rage';
import { Layouts } from '~/shared';
import { CarPreview } from '../../components/CarPreview';
import { Head } from '../../components/Head';
import { useLocalVehicle } from '../../hooks';
import {
	setCarMarketState,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import { CarMarketCreate } from '../CarMarketCreate';
import { CarMarketEdit } from '../CarMarketEdit';
import { CarMarketInfoPlayer } from '../CarMarketInfoPlayer';
import { CarMarketInfoSeller } from '../CarMarketInfoSeller';
import { CarMarketState, carMarketHeadTitle } from './CarMarket.model';
import {
	StyledCarPreviewContainer,
	StyledComponentContainer,
	StyledContainer,
} from './CarMarket.styled';

const carMarketComponents = {
	[CarMarketState.Create]: CarMarketCreate,
	[CarMarketState.InfoPlayer]: CarMarketInfoPlayer,
	[CarMarketState.InfoSeller]: CarMarketInfoSeller,
	[CarMarketState.Edit]: CarMarketEdit,
};

const CarMarket = () => {
	const dispatch = useLayoutDispatch();
	const carMarketState = useLayoutSelector(
		(state) => state.carMarket.carMarketState,
	);
	const isCreatingNewAdvert = useObservable(
		rage.vehicleMarket.getIsCreatingNewAdvert(),
	);
	const currentAdvert = useObservable(rage.vehicleMarket.getCurrentAdvert());
	const localPlayerNickname = useObservable(rage.player.getNickname());
	const { modelId, numberPlate } = useLocalVehicle();
	const governmentPrice = useMemo(() => {
		if (!modelId) return 0;
		return rage.vehicles.getGovernmentPriceByModel(modelId);
	}, [modelId]);

	const ActiveComponent = useMemo(
		() => carMarketComponents[carMarketState],
		[carMarketState],
	);

	const price = useMemo(
		() => (carMarketState !== CarMarketState.Create ? currentAdvert?.price : 0),
		[carMarketState, currentAdvert],
	);

	useEffect(() => {
		if (isCreatingNewAdvert) {
			dispatch(setCarMarketState(CarMarketState.Create));
			return;
		}

		if (localPlayerNickname === currentAdvert?.ownerNickname) {
			dispatch(setCarMarketState(CarMarketState.InfoSeller));
			return;
		}

		dispatch(setCarMarketState(CarMarketState.InfoPlayer));
	}, [isCreatingNewAdvert, localPlayerNickname, currentAdvert]);

	useEffectOnce(() => {
		// Перетаскивание
		const handleDragging = (e: any) => {
			e.preventDefault();
		};
		document.addEventListener('dragstart', handleDragging);
		return () => document.removeEventListener('dragstart', handleDragging);
	});

	return (
		<StyledContainer className={Layouts.CarMarket}>
			<Head
				title={carMarketHeadTitle[carMarketState]}
				canEdit={carMarketState === CarMarketState.InfoSeller}
				expiresAt={currentAdvert?.expiresAt}
			/>
			<StyledCarPreviewContainer>
				<CarPreview
					price={
						carMarketState === CarMarketState.Create ? governmentPrice : price
					}
					vehicleId={
						carMarketState === CarMarketState.Create
							? modelId ?? undefined
							: currentAdvert?.vehicleModel
					}
					numberPlate={
						carMarketState === CarMarketState.Create
							? numberPlate ?? undefined
							: currentAdvert?.numberPlate
					}
				/>
			</StyledCarPreviewContainer>
			<StyledComponentContainer>
				<ActiveComponent />
			</StyledComponentContainer>
		</StyledContainer>
	);
};

export { CarMarket };
